<template>
  <footer
    ref="$el"
    class="the-footer"
    role="contentinfo"
    aria-label="Site footer">
    <!-- Language Switcher -->
    <section
      class="the-footer__lang-section"
      role="group"
      aria-label="Language selection">
      <client-only>
        <div class="the-footer__lang-switcher">
          <template
            v-for="l in availableLocales"
            :key="l.code">
            <nuxt-link
              v-if="switchLocalePath(l.code)"
              :to="switchLocalePath(l.code)"
              :class="[
                'the-footer__lang-btn',
                { 'the-footer__lang-btn--active': l.code === locale },
              ]"
              :aria-current="l.code === locale ? 'page' : undefined"
              :aria-label="t('footer.currentLanguage', { language: l.name })"
              @click="handleLocaleLinkClick($event, l.code)">
              {{ l.code.toUpperCase() }}
            </nuxt-link>

            <span
              v-else
              class="the-footer__lang-btn the-footer__lang-btn--disabled">{{ l.code }}</span>
          </template>
        </div>
      </client-only>
    </section>

    <!-- Desktop Layout -->
    <div
      class="the-footer__desktop"
      role="navigation"
      aria-label="Footer navigation">
      <div class="the-footer__desktop-grid">
        <!-- Main Content Columns (Links) -->
        <div
          v-for="section in footerSections.slice(0, 5)"
          :key="section.id"
          class="the-footer__column">
          <p
            :id="`footer-section-${section.id}`"
            class="the-footer__column-title">
            {{ section.title }}
          </p>
          <ul
            class="the-footer__column-list"
            role="list"
            :aria-labelledby="`footer-section-${section.id}`">
            <li
              v-for="(item, index) in section.items"
              :key="item.id"
              class="the-footer__column-item"
              role="listitem">
              <nuxt-link
                :to="getFooterItemLink(section.id, index)"
                :target="getFooterItemTarget(section.id, index)"
                class="the-footer__column-link"
                :aria-label="item.ariaLabel || item.label">
                {{ item.label }}
              </nuxt-link>
            </li>
          </ul>
        </div>

        <!-- Social Column -->
        <aside
          class="the-footer__social-column"
          role="complementary"
          aria-label="Social media and copyright">
          <nav
            class="the-footer__social-nav"
            role="navigation"
            aria-label="Social media links">
            <p
              id="footer-social-title"
              class="the-footer__column-title">
              {{ $t('footer.followUsOn') }}
            </p>
            <ul
              class="the-footer__column-list"
              role="list"
              aria-labelledby="footer-social-title">
              <li
                v-for="social in socialLinks"
                :key="social.id"
                class="the-footer__column-item"
                role="listitem">
                <a
                  :href="social.href"
                  class="the-footer__column-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  :aria-label="`Follow us on ${social.label} (opens in new tab)`"
                  @click="onContactClick(social.label?.toLowerCase())">
                  {{ social.label }}
                </a>
              </li>
            </ul>
          </nav>
          <div
            class="the-footer__desktop-copyright"
            role="group"
            aria-label="Copyright information">
            <span class="the-footer__desktop-copyright-top">{{ $t('footer.copyright', { year: new Date().getFullYear() }) }}</span>
            <span class="the-footer__desktop-copyright-bottom">
              <base-link
                href="https://www.we-go.it/it/"
                target="_blank"
                class="the-footer__credits"
                rel="noopener noreferrer">
                <svg-bolded-by />
              </base-link>
            </span>
          </div>
        </aside>

        <!-- Branding Section -->
        <div
          class="the-footer__branding"
          role="group"
          aria-label="Company information">
          <div class="the-footer__logo">
            <nuxt-link
              :to="localePath('index')"
              class="the-footer__logo-link"
              aria-label="Return to homepage">
              <logo-icon class="the-footer__logo-svg" />
            </nuxt-link>
          </div>
          <nav
            class="the-footer__legal-links"
            role="navigation"
            aria-label="Legal and policy links">
            <template
              v-for="link in legalLinks"
              :key="link.id">
              <a
                v-if="link.href.startsWith('http') || link.href.startsWith('/pdfs/')"
                :href="link.href"
                class="the-footer__link"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="link.ariaLabel || link.label">
                {{ link.label }}
              </a>
              <nuxt-link
                v-else
                :to="link.href"
                class="the-footer__link"
                :aria-label="link.ariaLabel || link.label">
                {{ link.label }}
              </nuxt-link>
            </template>
          </nav>
        </div>
      </div>
    </div>

    <!-- Mobile Layout -->
    <nav
      class="the-footer__mobile"
      role="navigation"
      aria-label="Mobile footer navigation">
      <!-- Expandable Sections -->
      <div
        v-for="(section, index) in footerSections"
        :key="section.id"
        class="the-footer__mobile-section">
        <button
          :id="`mobile-toggle-${section.id}`"
          class="the-footer__mobile-header"
          :aria-expanded="expandedSections[index]"
          :aria-controls="`mobile-content-${section.id}`"
          :aria-label="`Toggle ${section.title} section`"
          @click="toggleSection(index)">
          <div
            class="the-footer__mobile-icon"
            aria-hidden="true">
            <span class="the-footer__mobile-icon-horizontal"></span>
            <span
              class="the-footer__mobile-icon-vertical"
              :class="{ 'the-footer__mobile-icon-vertical--hidden': expandedSections[index] }">
            </span>
          </div>
          <span class="the-footer__mobile-title">
            {{ section.title }}
          </span>
        </button>
        <div
          :id="`mobile-content-${section.id}`"
          class="the-footer__mobile-content"
          :class="{ 'the-footer__mobile-content--expanded': expandedSections[index] }"
          :aria-labelledby="`mobile-toggle-${section.id}`"
          :aria-hidden="!expandedSections[index]">
          <ul
            class="the-footer__mobile-list"
            role="list">
            <li
              v-for="(item, index) in section.items"
              :key="item.id"
              class="the-footer__mobile-item"
              role="listitem">
              <nuxt-link
                :to="getFooterItemLink(section.id, index)"
                :target="getFooterItemTarget(section.id, index)"
                class="the-footer__mobile-link"
                :aria-label="item.ariaLabel || item.label">
                {{ item.label }}
              </nuxt-link>
            </li>
          </ul>
        </div>
      </div>

      <!-- Mobile Footer Links -->
      <nav
        class="the-footer__mobile-links"
        role="navigation"
        aria-label="Legal links">
        <div class="the-footer__mobile-links-grid">
          <div class="the-footer__mobile-links-col">
            <template
              v-for="link in legalLinks.slice(0, Math.ceil(legalLinks.length / 2))"
              :key="link.id">
              <a
                v-if="link.href.startsWith('http') || link.href.startsWith('/pdfs/')"
                :href="link.href"
                class="the-footer__mobile-link"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="link.ariaLabel || link.label">
                {{ link.label }}
              </a>
              <nuxt-link
                v-else
                :to="link.href"
                class="the-footer__mobile-link"
                :aria-label="link.ariaLabel || link.label">
                {{ link.label }}
              </nuxt-link>
            </template>
          </div>
          <div class="the-footer__mobile-links-col">
            <template
              v-for="link in legalLinks.slice(Math.ceil(legalLinks.length / 2))"
              :key="link.id">
              <a
                v-if="link.href.startsWith('http') || link.href.startsWith('/pdfs/')"
                :href="link.href"
                class="the-footer__mobile-link"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="link.ariaLabel || link.label">
                {{ link.label }}
              </a>
              <nuxt-link
                v-else
                :to="link.href"
                class="the-footer__mobile-link"
                :aria-label="link.ariaLabel || link.label">
                {{ link.label }}
              </nuxt-link>
            </template>
          </div>
        </div>
      </nav>

      <!-- Mobile Branding -->
      <div
        class="the-footer__mobile-branding"
        role="group"
        aria-label="Company branding">
        <!-- Mobile Logo -->
        <div class="the-footer__mobile-logo">
          <nuxt-link
            :to="localePath('index')"
            class="the-footer__mobile-logo-link"
            aria-label="Return to homepage">
            <logo-icon class="the-footer__mobile-logo-svg" />
          </nuxt-link>
        </div>

        <!-- Mobile Copyright -->
        <div
          class="the-footer__mobile-copyright"
          role="group"
          aria-label="Copyright information">
          <span class="the-footer__mobile-copyright-top">{{ $t('footer.copyright', { year: new Date().getFullYear() }) }}</span>
          <span class="the-footer__mobile-copyright-bottom">
            <base-link
              href="https://www.we-go.it/it/"
              target="_blank"
              class="the-footer__credits"
              rel="noopener noreferrer">
              <svg-bolded-by />
            </base-link>
          </span>
        </div>
      </div>
    </nav>
  </footer>
</template>

<script setup>
  import { useIntersectionObserver } from '@vueuse/core'
  import { useI18n } from 'vue-i18n'

  import SvgBoldedBy from '~/assets/svgs/bolded-by.svg?component'
  import LogoIcon from '~/assets/svgs/logo.svg'
  import { pushDataLayer } from '~/utils/tracking'

  const layout = useLayoutStore()

  const switchLocalePath = useSwitchLocalePath()

  const {
    t, locale, locales
  } = useI18n()
  const localePath = useLocalePath()
  const route = useRoute()

  // Helper function to create locale-aware paths that work in new tabs
  const getLocalizedPath = path => {
    const currentLocale = locale.value || 'it'
    const localePrefix = currentLocale.split('_')[0] // Convert 'it' to 'it'
    return `/${localePrefix}${path}`
  }

  // Reactive state
  const expandedSections = reactive({
    0: false,
    1: false,
    2: false,
    3: false,
    4: false
  })

  // Available locales (imported from i18n configuration like in Menu.vue)
  const availableLocales = computed(() => {
    if (!locales.value) {
      return []
    }

    if (Array.isArray(locales.value)) {
      return locales.value?.filter(l => !l.hidden)
    }

    return []
  })

  const currentRoute = useRoute()
  const env = useRuntimeConfig().public

  const onContactClick = type => {
    pushDataLayer({
      page_path: currentRoute?.fullPath,
      page_location: `${env?.BASE_URL}${currentRoute?.fullPath}`,
      page_referrer: !!route?.from && route?.from?.fullPath ? `${env.BASE_URL}${route?.from.fullPath}` : document?.referrer,
      ...(type == 'instagram'
        ? {
          event: 'click_instagram_header'
        }
        : {}),
      ...(type == 'facebook'
        ? {
          event: 'click_facebook_header'
        }
        : {}),
      ...(type == 'linkedin'
        ? {
          event: 'click_linkedin_header'
        }
        : {}),
      ...(type == 'youtube'
        ? {
          event: 'click_youtube_header'
        }
        : {}),
      ...(type == 'tiktok'
        ? {
          event: 'click_tiktok_header'
        }
        : {}),
      ...(type == 'pinterest'
        ? {
          event: 'click_pinterest_header'
        }
        : {})

    })
  }

  // Helper function to normalize locale codes for comparison
  // const normalizeLocaleCode = value => {
  //   if (!value) {
  //     return ''
  //   }

  //   return value.toString().toLowerCase().replace('-', '_')
  // }

  // // Check if a locale is currently active
  // const isLocaleActive = code => {
  //   const normalizedTarget = normalizeLocaleCode(code)
  //   const normalizedCurrent = normalizeLocaleCode(locale.value)

  //   if (!normalizedTarget || !normalizedCurrent) {
  //     return false
  //   }

  //   return (
  //     normalizedCurrent === normalizedTarget
  //     || normalizedCurrent.startsWith(`${normalizedTarget}_`)
  //   )
  // }

  // // Function to generate language links
  // const generateLanguageLinks = () => {
  //   return availableLocales.value.map(lang => {
  //     const path = switchLocalePath(lang.code)
  //     const isActive = isLocaleActive(lang.code)

  //     return {
  //       code: lang.code,
  //       name: lang.name,
  //       path: path || route.fullPath,
  //       isActive
  //     }
  //   })
  // }

  // // Reactive ref for language links
  // const languageLinks = ref(generateLanguageLinks())

  // // Watch for locale and route changes to regenerate links
  // watch([ locale, () => route.fullPath ], () => {
  //   languageLinks.value = generateLanguageLinks()
  // }, { immediate: true })

  // Footer sections with i18n translations (compatible with existing template)
  const footerSections = computed(() => [
    {
      id: 'company',
      title: t('menu.about.title'),
      items: [
        { id: 'about-1', label: t('header.company') },
        { id: 'about-2', label: t('header.awards') },
        { id: 'about-3', label: t('header.careers') },
        { id: 'about-4', label: t('header.certifications') }
      ]
    },
    {
      id: 'dealers',
      title: t('footer.dealers'),
      items: [
        { id: 'dealers-1', label: t('header.italy') },
        { id: 'dealers-2', label: t('header.abroad') },
        { id: 'dealers-3', label: t('header.dealerInitiatives') }
      ]
    },
    {
      id: 'communication',
      title: t('footer.communication'),
      items: [
        { id: 'comm-1', label: t('header.magazine') },
        { id: 'comm-2', label: t('header.news') },
        { id: 'comm-3', label: t('header.pressReview') }
      ]
    },
    {
      id: 'support',
      title: t('footer.contactSupport'),
      items: [
        { id: 'support-1', label: t('header.contact') },
        { id: 'support-2', label: t('header.warranty') },
        { id: 'support-3', label: t('header.postSaleSupport') },
        { id: 'support-4', label: t('header.faq') },
        { id: 'support-5', label: t('header.maintenanceTips') }
      ]
    },
    {
      id: 'download',
      title: t('header.download'),
      items: [ { id: 'download-1', label: t('header.catalogs') }, { id: 'download-2', label: t('header.manuals') } ]
    }
  ])

  // Social media links
  const socialLinks = [
    {
      id: 'facebook', label: t('header.socialMedia.facebook'), href: 'https://www.facebook.com/VenetaCucine/'
    },
    {
      id: 'instagram', label: t('header.socialMedia.instagram'), href: 'https://www.instagram.com/venetacucine/'
    },
    {
      id: 'linkedin', label: t('header.socialMedia.linkedin'), href: 'https://www.linkedin.com/company/veneta-cucine-spa'
    },
    {
      id: 'pinterest', label: t('header.socialMedia.pinterest'), href: 'https://it.pinterest.com/venetacucine/'
    },
    {
      id: 'tiktok', label: 'TikTok', href: 'https://www.tiktok.com/@venetacucineofficial?_r=1&_t=ZN-93QEIYSnE5m'
    },
    {
      id: 'youtube', label: t('header.socialMedia.youtube'), href: 'https://www.youtube.com/user/venetacucinechannel'
    }
  ]

  // Legal links
  const legalLinks = computed(() => [
    {
      id: 'privacy', label: t('footer.privacyPolicy'), href: localePath('informativa-privacy')
    },
    {
      id: 'company', label: t('footer.companyInfo'), href: localePath('company-info')
    },
    {
      id: 'code-of-conduct',
      label: t('footer.codeOfConduct'),
      href: locale.value === 'it'
        ? '/pdfs/veneta_cucine_codice_di_comportamento_di_gruppo_2025.pdf'
        : '/pdfs/veneta_cucine_allegato_5_codice_di_comportamento_2022_en.pdf'
    },
    {
      id: 'whistleblowing', label: t('footer.whistleblowing'), href: '/pdfs/veneta_cucine_whistleblowing_2025.pdf'
    },
    {
      id: 'cookies', label: t('footer.cookiesInfo'), href: localePath('cookie-policy')
    },
    {
      id: 'netiquette', label: 'Netiquette Social Network', href: localePath('netiquette')
    }
  ])

  // Methods
  const toggleSection = index => {
    // Close all other sections first (accordion behavior)
    Object.keys(expandedSections).forEach(key => {
      if (parseInt(key) !== index) {
        expandedSections[key] = false
      }
    })

    // Toggle the clicked section
    expandedSections[index] = !expandedSections[index]
  }

  // Check if the click is a modified click (cmd/ctrl/shift/middle click)
  const isModifiedClick = event => {
    if (!event) {
      return false
    }

    return (
      event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1
    )
  }

  // Handle locale link clicks
  const handleLocaleLinkClick = (event, code) => {
    // Allow modified clicks to open in new tab
    if (isModifiedClick(event)) {
      return
    }

    // Prevent clicking on the already active locale
    if (code === locale.value) {
      event?.preventDefault?.()
      return
    }
  }

  // // Watch for locale changes to refresh global data
  // watch(locale, async () => {
  //   try {
  //     const globalStore = useGlobalStore()
  //     const navigationStore = useNavigationStore()
  //     await Promise.all([ navigationStore.getNavigations(), globalStore.getData() ])
  //   } catch (e) {
  //     console.error('Error refreshing data on locale change', e)
  //   }
  // })

  // Get access to the event bus
  const { bus } = useNuxtApp()
  const $el = useTemplateRef('$el')

  // Handle link clicks - scroll to top
  // const handleLinkClick = event => {
  //   // If it's a middle click or a click with modifier keys, don't scroll
  //   if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1)) {
  //     return
  //   }

  //   // Scroll to top on navigation using the event bus
  //   // nextTick(() => {
  //   //   bus.emit('scroll:scroll-to', {
  //   //     target: 'top',
  //   //     options: {
  //   //       immediate: true
  //   //     }
  //   //   })
  //   // })
  // }

  // Get link for footer items based on section and index
  const getFooterItemLink = (sectionId, itemIndex) => {
    // Route names for i18n localePath resolution
    const routeNames = {
      company: [
        'company-about',
        'awards',
        // careers: piattaforma esterna Lavora con noi (vedi fallbackPaths)
        null,
        'certifications'
      ],
      dealers: [
        'store-locator-italy-region-province',
        'store-locator-abroad-continent-country',
        'initiatives'
      ],
      communication: [
        'magazine',
        'news',
        'press'
      ],
      support: [
        'contacts',
        'warranty',
        'after-sales-support',
        'faq',
        'maintenance'
      ],
      download: [ 'catalogues', 'manuals' ]
    }

    // Fallback paths for routes without i18n config
    const fallbackPaths = {
      company: [
        null,
        null,
        'https://lavoraconnoi.venetacucine.com/',
        null
      ],
      dealers: [
        null,
        null,
        null
      ],
      communication: [
        null,
        null,
        null
      ],
      support: [
        null,
        null,
        null,
        null,
        null
      ],
      download: [ null, null ]
    }

    const routeName = routeNames[sectionId]?.[itemIndex]
    const fallbackPath = fallbackPaths[sectionId]?.[itemIndex]

    if (routeName) {
      return localePath(routeName)
    }

    if (fallbackPath) {
      // Don't localize hash links or external URLs
      if (fallbackPath.startsWith('#') || fallbackPath.startsWith('http')) {
        return fallbackPath
      }
      return getLocalizedPath(fallbackPath)
    }

    return '/'
  }

  // Le voci che puntano a URL esterni si aprono in una nuova scheda
  // (NuxtLink aggiunge da solo rel="noopener" ai link esterni con target).
  const getFooterItemTarget = (sectionId, itemIndex) => {
    const to = getFooterItemLink(sectionId, itemIndex)
    return typeof to === 'string' && to.startsWith('http') ? '_blank' : undefined
  }

  // Recalculate scroll when Veneta Cucine footer is on screen
  const handleIntersect = ([ { isIntersecting } ]) => {
    if (isIntersecting) {
      bus?.emit?.('scroll:refresh')
    }
  }

  useIntersectionObserver($el, handleIntersect, {
    rootMargin: '0px',
    threshold: 0.25
  })
</script>

<style lang="scss" scoped>
.the-footer {
  background-color: var(--color-light-grey);
  padding: 0;

  /* Language Switcher */
  &__lang-section {
    display: flex;
    justify-content: flex-end;
    padding: var(--space-xxl) var(--space-s) var(--space-s);

    @include mq(desktop) {
      justify-content: center;
      padding: var(--space-giga) var(--space-m) var(--space-xxxl);
    }
  }

  &__lang-switcher {
    display: flex;
    justify-content: center;
    gap: var(--space-s);
    flex-wrap: wrap;
  }

  &__lang-btn {
    font-size: 14px;
    min-width: 24px;
    height: 40px;

    @include body-xs;

    color: var(--color-dark-grey);
    background: none;
    border: none;
    cursor: pointer;
    text-transform: uppercase;
    padding: var(--space-xxs) var(--space-xs);
    border-radius: var(--space-xxxs);
    transition:
      color var(--timing-fast) var(--easing-default),
      background-color var(--timing-fast) var(--easing-default);

    &--active {
      color: var(--color-black);
      font-weight: var(--font-weight-bold);
      text-decoration: underline;
      background-color: transparent;
    }

    &--disabled {
      color: var(--colors-dark-grey);
      cursor: not-allowed;
    }

    &:hover {
      background-color: rgb(var(--color-red), 0.05);
    }

    &:focus-visible {
      outline: 2px solid var(--color-red);
      outline-offset: 2px;
    }
  }

  /* Desktop Layout */
  &__desktop {
    display: none;

    @include mq(desktop) {
      display: block;
      max-width: var(--size-width-margin);
      margin: 0 auto;
      padding: 0 var(--space-m) var(--space-s);
    }
  }

  &__desktop-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-template-rows: auto auto;
    gap: var(--space-xl) 0;

    /* We handle spacing via padding/borders to ensure equal columns */
  }

  &__column {
    /* Ensure border is included in width calculation */
    box-sizing: border-box;
    border-left: 1px solid var(--color-grey);
    padding-left: var(--space-s);

    /* Remove border for the first column */
    &:first-child {
      border-left: none;
      padding-left: 0;
    }
  }

  &__column-title {
    font-weight: var(--font-weight-bold);
    color: var(--color-red);
    margin: 0 0 var(--space-n);
    font-size: 16px;
    padding: 0;

    // text-transform: uppercase;
  }

  &__column-list {
    @include neutralize-list;
  }

  &__column-item {
    margin-bottom: var(--space-xs);
    padding: 0 8px 0 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__column-link {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &.router-link-exact-active {
      text-decoration: underline;
    }
  }

  &__column-link,
  &__column-text {
    @include body-xs;

    color: var(--color-dark-grey);
    text-decoration: none;
    display: block;
    padding: var(--space-xxxxs) 0;
    transition: color var(--timing-fast) var(--easing-default);
    position: relative;
    width: fit-content;

    &::after {
      content: "";
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 1px;
      background-color: currentcolor;
      opacity: 0;
      transition: opacity 0.3s ease-in-out;
    }

    &:hover {
      color: var(--color-dark-grey);

      &::after {
        opacity: 1;
      }
    }

    &:focus-visible {
      outline: 2px solid var(--color-red);
      outline-offset: 2px;
      border-radius: var(--space-xxxxs);
    }
  }

  &__column-text {
    cursor: default;

    &:hover {
      color: var(--color-dark-grey);
    }
  }

  /* Social Column - Spans 2 rows on the right */
  &__social-column {
    grid-column: 6;
    grid-row: 1 / span 2;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border-left: 1px solid var(--color-grey);
    padding-left: var(--space-s);
    height: 100%;

    /* Ensure it takes full height */
  }

  &__social-nav {
    /* Wrapper for social links part */
  }

  /* Branding Section - Spans 4 columns on the second row */
  &__branding {
    grid-column: 1 / span 4;
    grid-row: 2;
    display: flex;
    flex-direction: column;
    gap: var(--space-m);
    margin-top: var(--space-giga);

    /* Align with the grid flow */
    padding-right: var(--space-s);
  }

  &__logo {
    display: flex;
    align-items: center;
    justify-content: flex-start;

    &-link {
      display: block;
      text-decoration: none;
      width: 100%;
      transition: opacity var(--timing-fast) var(--easing-default);

      &:hover {
        opacity: 0.8;
      }

      &:focus-visible {
        outline: 2px solid var(--color-red);
        outline-offset: 2px;
        border-radius: var(--space-xxxxs);
      }
    }

    a.router-link-exact-active {
      opacity: 1;
    }

    &-svg {
      width: 100%;
      height: auto;
      color: var(--color-dark-grey);
    }
  }

  &__legal-links {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-s);
  }

  &__link {
    @include body-xs;

    color: var(--color-dark-grey);
    text-decoration: none;
    padding: var(--space-xxxxs) 0;
    transition: color var(--timing-fast) var(--easing-default);
    position: relative;

    &::after {
      content: "";
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 1px;
      background-color: currentcolor;
      opacity: 0;
      transition: opacity 0.3s ease-in-out;
    }

    &:hover {
      color: var(--color-dark-grey);

      &::after {
        opacity: 1;
      }
    }

    &:focus-visible {
      outline: 2px solid var(--color-red);
      outline-offset: 2px;
      border-radius: var(--space-xxxxs);
    }
  }

  &__desktop-copyright {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    opacity: 0.6;
    margin-top: auto;

    /* Push to bottom if needed */

    &-top,
    &-bottom {
      color: var(--color-black);

      @include body-xs;

      line-height: 1.4;
    }

    &-bottom {
      font-family: var(--font-wego, var(--font-family-poppins));
    }
  }

  &__credits {
    > svg {
      height: 12px;
      fill: currentcolor;
      transition: fill var(--timing-default);
    }

    &:hover {
      > svg {
        fill: #f1b7ff;
      }
    }
  }

  /* &__wego-link {
    color: inherit;
    text-decoration: none;
    font-family: var(--font-wego, var(--font-family-poppins));
    transition: opacity var(--timing-fast) var(--easing-default);

    &:hover {
      opacity: 0.7;
    }

    &:focus-visible {
      outline: 2px solid var(--color-red);
      outline-offset: 2px;
      border-radius: var(--space-xxxxs);
    }

    strong {
      font-family: var(--font-wego, var(--font-family-poppins));
      font-weight: 700;
    }
  } */

  /* Mobile Layout */
  &__mobile {
    display: block;
    padding: 0 var(--space-s) var(--space-n);

    @include mq(desktop) {
      display: none;
    }
  }

  &__mobile-section {
    display: flex;
    flex-direction: column;
    gap: 0;
    border-bottom: 1px solid var(--color-grey);
    padding: 0;
  }

  &__mobile-header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
    width: 100%;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    transition:
      background-color var(--timing-fast) var(--easing-default),
      padding-bottom var(--timing-fast) var(--easing-default);

    &[aria-expanded="true"] {
      padding-bottom: 0;
    }

    &:hover {
      background-color: transparent;
    }

    &:focus-visible {
      outline: 2px solid var(--color-red);
      outline-offset: 2px;
      border-radius: var(--space-xxxxs);
    }
  }

  &__mobile-icon {
    position: relative;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__mobile-icon-horizontal {
    position: absolute;
    width: 12px;
    height: 2px;
    background-color: var(--color-red);
    transition: transform 0.3s ease;
  }

  &__mobile-icon-vertical {
    position: absolute;
    width: 2px;
    height: 12px;
    background-color: var(--color-red);
    transition: transform 0.3s ease, opacity 0.3s ease;

    &--hidden {
      transform: rotate(90deg);
      opacity: 0;
    }
  }

  &__mobile-title {
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 160%;
    color: var(--color-red, #dc0814);
    margin: 0;
    user-select: none;
    text-transform: uppercase;
    padding-top: 16px !important;
    padding-bottom: 14px !important;

    // background-color: red;
  }

  &__mobile-content {
    max-height: 0;
    opacity: 0;
    padding: 0;
    overflow: hidden;
    transition: max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease;
    border-top: 1px solid transparent;
    padding-left: 36px;
  }

  &__mobile-content--expanded {
    max-height: 400px;
    opacity: 1;
    padding: 0 0 var(--space-s);
    padding-left: 36px;
  }

  &__mobile-list {
    @include neutralize-list;

    display: flex;
    flex-direction: column;
    gap: var(--spacers-space-xs, 16px);
  }

  &__mobile-item {
    padding: 0;
  }

  &__mobile-link {
    font-size: 14px;

    // @include body-xs;

    color: #000;
    text-decoration: none;
    text-transform: none;
    display: inline-block;
    padding: var(--space-xxxxs) 0;
    transition: color var(--timing-fast) var(--easing-default);
    position: relative;
    cursor: pointer;
    align-self: flex-start;

    &::after {
      content: "";
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 1px;
      background-color: currentcolor;
      opacity: 0;
      transition: opacity 0.3s ease-in-out;
    }

    &:hover {
      color: #000;

      &::after {
        opacity: 1;
      }
    }

    &:focus-visible {
      outline: 2px solid var(--color-red);
      outline-offset: 2px;
      border-radius: var(--space-xxxxs);
    }
  }

  &__mobile-links {
    margin-top: var(--space-xxl);
    margin-bottom: var(--space-xl);
  }

  &__mobile-links-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-xs);
  }

  &__mobile-links-col {
    display: flex;
    flex-direction: column;
    gap: var(--space-xs);

    &:last-child {
      padding-left: var(--space-xxs);
    }
  }

  &__mobile-branding {
    text-align: center;
  }

  &__mobile-logo {
    margin-bottom: var(--space-s);

    &-link {
      display: inline-block;
      text-decoration: none;
      transition: opacity var(--timing-fast) var(--easing-default);

      &:hover {
        opacity: 0.8;
      }

      &:focus-visible {
        outline: 2px solid var(--color-red);
        outline-offset: 2px;
        border-radius: var(--space-xxxxs);
      }
    }

    &-svg {
      width: 60vw;
      max-width: 900px;
      height: auto;
      color: var(--color-dark-grey);
    }
  }

  &__mobile-copyright {
    display: flex;
    flex-direction: column;
    gap: var(--space-xxs);
    opacity: 0.6;

    &-top,
    &-bottom {
      color: var(--color-black);

      @include body-xs;

      line-height: 1.4;
    }

    &-bottom {
      font-family: var(--font-wego, var(--font-family-poppins));
    }
  }
}
</style>
